import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Divider,
  Link,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { apiAssetUrl } from "../utils/apiAssetUrl";
import {
  countLocationSectionsProgress,
  normalizeLocationDetailsFromApi,
} from "../utils/locationProductDetails";

function Section({ title, iconClass, children }) {
  return (
    <Box borderWidth="1px" borderRadius="xl" borderColor="gray.100" p={4} bg="gray.50">
      <HStack mb={3} spacing={2}>
        <Box as="i" className={`fa-solid ${iconClass}`} color="brand.500" />
        <Text fontWeight="700" fontSize="md">
          {title}
        </Text>
      </HStack>
      {children}
    </Box>
  );
}

function Row({ label, value }) {
  if (value === "" || value == null || value === false) return null;
  const display =
    typeof value === "boolean"
      ? value
        ? "Yes"
        : "No"
      : Array.isArray(value)
        ? value.join(", ")
        : String(value);
  if (!display) return null;
  return (
    <HStack align="start" justify="space-between" py={1} fontSize="sm">
      <Text color="gray.600" flex="0 0 48%">
        {label}
      </Text>
      <Text fontWeight="500" textAlign="right" flex="1">
        {display}
      </Text>
    </HStack>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.productName
 * @param {unknown} props.locationDetails raw from API
 * @param {string} props.apiBase
 */
export default function LocationDetailsReadOnlyModal({ isOpen, onClose, productName, locationDetails, apiBase }) {
  const loc = normalizeLocationDetailsFromApi(locationDetails);
  const perm = loc.permissions || {};
  const pdf = (path) => (path && apiBase ? apiAssetUrl(apiBase, path) : "");
  const hasAny = countLocationSectionsProgress(loc) > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" maxH="90vh">
        <ModalHeader borderBottomWidth="1px" pb={3}>
          <Text fontSize="lg" fontWeight="800">
            {productName || "Location details"}
          </Text>
          <Text fontSize="sm" color="gray.500" fontWeight="normal">
            Full venue / location specification
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6} px={{ base: 4, md: 6 }}>
          <VStack align="stretch" spacing={5}>
            {!hasAny ? (
              <Text fontSize="sm" color="gray.600" bg="orange.50" borderRadius="md" p={3}>
                Extended location fields have not been completed for this listing yet.
              </Text>
            ) : null}
            <Section title="Basic Amenities" iconClass="fa-bolt">
              <Row label="Power backup" value={loc.amenities?.powerBackup} />
              {(loc.amenities?.powerBackupTypes || []).length ? (
                <Row label="Backup type" value={loc.amenities.powerBackupTypes} />
              ) : null}
              <Row label="Electric power" value={loc.amenities?.electricPowerAvailable} />
              {loc.amenities?.electricPowerAvailable ? (
                <Row label="Per unit cost (₹)" value={loc.amenities?.electricPerUnitCost} />
              ) : null}
              <Row label="Water supply" value={loc.amenities?.waterSupply} />
              <Row label="WiFi" value={loc.amenities?.wifi} />
              <Row label="CCTV" value={loc.amenities?.cctv} />
              <Row label="Security 24×7" value={loc.amenities?.security24x7} />
            </Section>

            <Section title="Facilities & Features" iconClass="fa-building">
              {(loc.facilities || []).length ? (
                <Wrap>
                  {(loc.facilities || []).map((f) => (
                    <WrapItem key={f}>
                      <Badge colorScheme="brand" borderRadius="full" px={2}>
                        {f}
                      </Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  None selected
                </Text>
              )}
            </Section>

            <Section title="Permissions & Legal" iconClass="fa-file-contract">
              <Row label="Permission required" value={perm.permissionRequired !== false} />
              {perm.permissionRequired !== false ? (
                <>
                  <Row label="Location permission" value={perm.locationPermission} />
                  <Row label="Drone permission" value={perm.dronePermission} />
                  <Divider my={2} />
                  {[
                    ["NOC certificate", perm.noc_certificate],
                    ["Business license", perm.business_license],
                    ["Fire safety", perm.fire_safety_certificate],
                    ["Local authority", perm.local_authority_permission],
                    ["Insurance", perm.insurance],
                  ].map(([label, pth]) =>
                    pth ? (
                      <Box key={label} py={1}>
                        <Link
                          href={pdf(pth)}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="brand.600"
                          fontWeight="600"
                          fontSize="sm"
                        >
                          <i className="fa-solid fa-file-pdf" style={{ marginRight: 6 }} />
                          {label} (PDF)
                        </Link>
                      </Box>
                    ) : null
                  )}
                </>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  Legal documents not required for this listing.
                </Text>
              )}
            </Section>

            <Section title="Space Details" iconClass="fa-ruler-combined">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Row label="Total area (sq ft)" value={loc.space?.totalAreaSqFt} />
                <Row label="Seating capacity" value={loc.space?.seatingCapacity} />
                <Row label="Standing capacity" value={loc.space?.standingCapacity} />
                <Row label="Furnishing" value={loc.space?.furnishingType} />
                <Row label="Rooms" value={loc.space?.numberOfRooms} />
                <Row label="Stay available" value={loc.space?.stayAvailable} />
                <Row
                  label="Floor / Total floors"
                  value={[loc.space?.floorNumber, loc.space?.totalFloors]
                    .filter((x) => x !== "" && x != null)
                    .join(" / ")}
                />
                <Row label="Facing" value={loc.space?.facingDirection} />
                <Row label="Ownership" value={loc.space?.ownershipType} />
              </SimpleGrid>
            </Section>

            <Section title="Pricing & Deposit" iconClass="fa-tag">
              <Row label="Security deposit (₹)" value={loc.pricing?.securityDeposit} />
            </Section>

            <Section title="Media & Links" iconClass="fa-link">
              {loc.media?.videoLink ? (
                <Link href={loc.media.videoLink} color="brand.600" isExternal fontWeight="600">
                  Watch video →
                </Link>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No video linked
                </Text>
              )}
            </Section>

            <Section title="Rules & Restrictions" iconClass="fa-ban">
              <Row label="Smoking" value={loc.rules?.smokingAllowed} />
              <Row label="Alcohol" value={loc.rules?.alcoholAllowed} />
              <Row label="Loud music" value={loc.rules?.loudMusicAllowed} />
              <Row label="Pets" value={loc.rules?.petsAllowed} />
              <Row label="Closing / curfew" value={loc.rules?.closingTime} />
              <Row label="Mobile coverage" value={loc.rules?.mobileNetworkCoverage} />
              {(loc.rules?.eventTypesAllowed || []).length ? (
                <Box pt={2}>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Events allowed
                  </Text>
                  <Wrap>
                    {(loc.rules.eventTypesAllowed || []).map((e) => (
                      <WrapItem key={e}>
                        <Badge borderRadius="full">{e}</Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              ) : null}
            </Section>

            <Section title="Parking & Transport" iconClass="fa-car">
              <Row label="2-wheeler slots" value={loc.parking?.parking2Wheeler} />
              <Row label="4-wheeler slots" value={loc.parking?.parking4Wheeler} />
              <Row label="8-wheeler slots" value={loc.parking?.parking8Wheeler} />
              <Row label="Helipad" value={loc.parking?.helipadAvailable} />
              <Row label="Private airstrip" value={loc.parking?.privateAirRunway} />
            </Section>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
